import {
  CommissionType,
  type CoverSheetConfigCard,
  CoverSheetIconType,
  type Layout,
} from '../../types';

type CommissionPrefix = 'commission' | 'counterpartyCommission';

export type CommissionFieldOptions = {
  label: string;
  additionalLabel: string;
  /** Only ever an accessible name — the toggle renders icons, not text. */
  typeLabel?: string;
  required?: boolean;
};

/**
 * Percent and dollar amounts occupy the same slot — only one is ever visible,
 * so the row still totals 100.
 */
export function commissionLayoutRow(prefix: CommissionPrefix): Layout[] {
  return [
    { name: `${prefix}AmountPercent`, size: 30 },
    { name: `${prefix}AmountDollar`, size: 30 },
    { name: `${prefix}Type`, size: 20 },
    { name: `${prefix}AdditionalAmount`, size: 50 },
  ];
}

export function commissionFields(
  prefix: CommissionPrefix,
  {
    label,
    additionalLabel,
    typeLabel = label,
    required = true,
  }: CommissionFieldOptions,
): CoverSheetConfigCard['fields'] {
  const typeName = `${prefix}Type`;
  const percentName = `${prefix}AmountPercent`;
  const dollarName = `${prefix}AmountDollar`;
  const additionalName = `${prefix}AdditionalAmount`;

  const requiredValidation = required
    ? { validation: [{ type: 'required', message: 'Required' }] }
    : {};

  return {
    [typeName]: {
      name: typeName,
      entity: 'interaction',
      type: 'iconButtonGroup',
      label: typeLabel,
      options: [
        {
          label: 'Percentage',
          value: CommissionType.PERCENTAGE,
          icon: CoverSheetIconType.percentage,
        },
        {
          label: 'Dollar Amount',
          value: CommissionType.AMOUNT,
          icon: CoverSheetIconType.dollar,
        },
      ],
      value: CommissionType.PERCENTAGE,
      ...requiredValidation,
      action: {
        name: 'reset',
        props: [
          { from: percentName, to: '' },
          { from: dollarName, to: '' },
        ],
      },
    },
    [percentName]: {
      name: percentName,
      entity: 'interaction',
      type: 'percentage',
      label,
      placeholderText: '',
      showWhen: {
        fieldName: typeName,
        hasValue: CommissionType.PERCENTAGE,
      },
      ...requiredValidation,
    },
    [dollarName]: {
      name: dollarName,
      entity: 'interaction',
      type: 'money',
      label,
      placeholderText: '0.00',
      showWhen: {
        fieldName: typeName,
        hasValue: CommissionType.AMOUNT,
      },
      ...requiredValidation,
    },
    [additionalName]: {
      name: additionalName,
      entity: 'interaction',
      type: 'money',
      label: additionalLabel,
      placeholderText: '',
    },
  };
}
